import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  Fab,
  useTheme,
  useMediaQuery,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  alpha,
  Tooltip,
  Snackbar,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  DeleteSweep as DeleteSweepIcon,
  Repeat as RepeatIcon,
  FileDownload as FileDownloadIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import { ROLE } from '../utils/roles';
import TimeOffCalendar from '../components/TimeOffCalendar';
import TimeOffRequestModal from '../components/TimeOffRequestModal';
import RecurringDateSelectionModal from '../components/RecurringDateSelectionModal';
import EventModal from '../components/EventModal';
import LoadingAnimation from '../components/LoadingAnimation';
import timeOffService from '../services/timeOffService';
import usersService from '../services/usersService';
import { TimeOffRequest, PublicHoliday, TimeOffFormData, User, Event } from '../types';
import { openFilePreviewOrDownload } from '../utils/filePreview';

const formatLocalYmd = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCalendarFetchWindow = (month: Date) => {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const start = new Date(firstDay);
  start.setDate(start.getDate() - 7);
  const end = new Date(lastDay);
  end.setDate(end.getDate() + 7);
  return { startDate: formatLocalYmd(start), endDate: formatLocalYmd(end) };
};

const mergeTimeOffRequests = (...lists: TimeOffRequest[][]): TimeOffRequest[] => {
  const map = new Map<number, TimeOffRequest>();
  for (const list of lists) {
    for (const req of list) {
      map.set(req.id, req);
    }
  }
  return Array.from(map.values());
};

const TimeOffPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Nautilus Console Design System Tokens
  const isDark = theme.palette.mode === 'dark';
  const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // State management
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [userTimeOffRequests, setUserTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  const [holidayBalances, setHolidayBalances] = useState<{ year: number; balance: number; year_category: string; category: string }[]>([]);
  const [leaveSummaries, setLeaveSummaries] = useState<Record<string, { paid_leave: number; leave_awaiting_deduction: number }>>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [recurringModalOpen, setRecurringModalOpen] = useState(false);
  const [userRequestsModalOpen, setUserRequestsModalOpen] = useState(false);
  const [pendingRequestsExpanded, setPendingRequestsExpanded] = useState(false);
  const [selectedPendingRequests, setSelectedPendingRequests] = useState<number[]>([]);
  const [approvedRequestsExpanded, setApprovedRequestsExpanded] = useState(false);
  const [selectedApprovedRequests, setSelectedApprovedRequests] = useState<number[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [offices, setOffices] = useState<{ id: number; country: string }[]>([]);
  const [departments, setDepartments] = useState<{ id: number; department: string }[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [birthdays, setBirthdays] = useState<User[]>([]);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Exclusion warning state
  const [excludedDates, setExcludedDates] = useState<string[]>([]);
  const [filteredDates, setFilteredDates] = useState<string[]>([]);
  const [showExclusionWarning, setShowExclusionWarning] = useState(false);

  // Export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState<'month' | 'range'>('month');
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');

  // Responsive font size for large screens (1200-1800px)
  const responsiveFontSize = {
    fontSize: '0.875rem',
    '@media (min-width: 1200px) and (max-width: 1800px)': {
      fontSize: '0.75rem',
    },
  };

  const fetchTimeOffRequestsForMonth = useCallback(async (month: Date, canApprove: boolean) => {
    const window = getCalendarFetchWindow(month);
    const [windowRequests, pendingRequests] = await Promise.all([
      timeOffService.getAllTimeOffRequests({
        startDate: window.startDate,
        endDate: window.endDate,
      }),
      canApprove
        ? timeOffService.getAllTimeOffRequests({ status: 'pending' })
        : Promise.resolve([] as TimeOffRequest[]),
    ]);
    return mergeTimeOffRequests(windowRequests, pendingRequests);
  }, []);

  const loadAdminExtras = useCallback(async () => {
    if (!user) return;
    const tasks: Promise<void>[] = [];
    if (hasCapability(user, CAP.SUBMIT_TIME_OFF_FOR_OTHERS)) {
      tasks.push(
        usersService.getUsers().then((allUsers) => {
          setUsers(allUsers as unknown as User[]);
        })
      );
    }
    tasks.push(usersService.getDepartments().then(setDepartments));
    await Promise.allSettled(tasks);
  }, [user]);

  const refreshTimeOffData = useCallback(async () => {
    if (!user) return;

    try {
      const canApprove = hasCapability(user, CAP.APPROVE_TIME_OFF);
      const [mergedRequests, userRequests, balances] = await Promise.all([
        fetchTimeOffRequestsForMonth(calendarMonth, canApprove),
        timeOffService.getTimeOffRequestsByUser(user.id),
        timeOffService.getUserHolidayBalances(),
      ]);
      setTimeOffRequests(mergedRequests);
      setUserTimeOffRequests(userRequests);
      setHolidayBalances(balances);
      if (canApprove) {
        const pendingUserIds = [...new Set(mergedRequests.filter((r) => r.status === 'pending').map((r) => r.userId))];
        setLeaveSummaries(await timeOffService.getLeaveSummaries(pendingUserIds));
      }
    } catch (err) {
      console.error('Error refreshing time off data:', err);
      setError('Failed to refresh time off data');
    }
  }, [user, calendarMonth, fetchTimeOffRequestsForMonth]);

  const initializeData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const currentYear = new Date().getFullYear();
      const canApprove = hasCapability(user, CAP.APPROVE_TIME_OFF);

      const [
        allHolidays,
        mergedRequests,
        userRequests,
        balances,
        officesData,
        eventsData,
        allBirthdays,
      ] = await Promise.all([
        timeOffService.getPublicHolidays({
          fromYear: currentYear - 1,
          toYear: currentYear + 2,
        }),
        fetchTimeOffRequestsForMonth(calendarMonth, canApprove),
        timeOffService.getTimeOffRequestsByUser(user.id),
        timeOffService.getUserHolidayBalances(),
        usersService.getOffices(),
        timeOffService.getEvents(),
        usersService.getBirthdays(),
      ]);

      setPublicHolidays(allHolidays);
      setTimeOffRequests(mergedRequests);
      setUserTimeOffRequests(userRequests);
      setHolidayBalances(balances);
      if (canApprove) {
        const pendingUserIds = [...new Set(mergedRequests.filter((r) => r.status === 'pending').map((r) => r.userId))];
        setLeaveSummaries(await timeOffService.getLeaveSummaries(pendingUserIds));
      }
      setOffices(officesData);
      setEvents(eventsData);
      setBirthdays(allBirthdays);
    } catch (err) {
      setError('Failed to load time off data');
      console.error('Error initializing time off data:', err);
    } finally {
      setLoading(false);
      void loadAdminExtras();
    }
  }, [user, calendarMonth, fetchTimeOffRequestsForMonth, loadAdminExtras]);

  useEffect(() => {
    void initializeData();
  }, [user]);

  const handleMonthChange = useCallback(async (date: Date) => {
    setCalendarMonth(date);
    if (!user) return;

    try {
      const canApprove = hasCapability(user, CAP.APPROVE_TIME_OFF);
      const mergedRequests = await fetchTimeOffRequestsForMonth(date, canApprove);
      setTimeOffRequests(mergedRequests);
    } catch (err) {
      console.error('Failed to refresh calendar window:', err);
    }
  }, [user, fetchTimeOffRequestsForMonth]);

  const isDateHoliday = (date: string) => {
    const userOfficeId = user?.user_office;
    // Map office ID to country
    const userOfficeCountry = offices.find(office => Number(office.id) === Number(userOfficeId))?.country;

    return publicHolidays.some(h => {
      if (h.date !== date) return false;

      if (!userOfficeCountry) return true;

      const holidayOffice = (h as any).office;
      if (holidayOffice) {
        return holidayOffice === userOfficeCountry || holidayOffice === 'ALL';
      }

      const isUK = h.name.includes('(UK)');
      const isFR = h.name.includes('(FR)');

      if (isUK || isFR) {
        if (userOfficeCountry === 'UK' && isUK) return true;
        if (userOfficeCountry === 'FR' && isFR) return true;
        return false;
      }

      return true;
    });
  };

  const handleDateSelect = (date: string) => {
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fifteenWeeksAgo = new Date(today);
    fifteenWeeksAgo.setDate(today.getDate() - 7 * 15);

    if (selectedDate < fifteenWeeksAgo) {
      setSnackbar({
        open: true,
        message: 'Cannot select dates more than 15 weeks in the past',
        severity: 'error'
      });
      return;
    }

    if (isDateHoliday(date)) {
      setSnackbar({
        open: true,
        message: 'Cannot select bank holiday dates for time off',
        severity: 'error'
      });
      return;
    }

    setSelectedDates(prev => {
      if (prev.includes(date)) {
        return prev.filter(d => d !== date);
      } else {
        return [...prev, date].sort();
      }
    });
  };

  const applyRecurringDates = (dates: string[]) => {
    setSelectedDates((prev) => {
      const newDates = [...prev];
      dates.forEach((date) => {
        if (!newDates.includes(date)) {
          newDates.push(date);
        }
      });
      return newDates.sort();
    });
  };

  const handleRecurringDatesSelected = (dates: string[]) => {
    const filtered = dates.filter((date) => !isDateHoliday(date));
    const excluded = dates.filter((date) => isDateHoliday(date));

    setFilteredDates(filtered);
    setExcludedDates(excluded);

    if (excluded.length > 0) {
      setShowExclusionWarning(true);
    } else {
      applyRecurringDates(filtered);
    }
  };

  const handleConfirmExclusion = () => {
    applyRecurringDates(filteredDates);
    setShowExclusionWarning(false);
    setExcludedDates([]);
    setFilteredDates([]);
  };

  const handleCancelExclusion = () => {
    setShowExclusionWarning(false);
    setExcludedDates([]);
    setFilteredDates([]);
  };

  const handleClearSelection = () => {
    setSelectedDates([]);
  };

  const handleRequestSubmit = async (formData: TimeOffFormData) => {
    if (!user) return;

    try {
      await timeOffService.submitTimeOffRequest(
        user.id,
        user.name,
        formData
      );

      setSelectedDates([]);
      await refreshTimeOffData();
      setSnackbar({
        open: true,
        message: 'Time off request submitted successfully',
        severity: 'success',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    }
  };

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);
  const handleUserRequestsModalOpen = () => setUserRequestsModalOpen(true);
  const handleUserRequestsModalClose = () => setUserRequestsModalOpen(false);

  const handleApproveRequest = async (requestId: number) => {
    try {
      await timeOffService.updateTimeOffRequest(requestId.toString(), { status: 'approved' });
      await refreshTimeOffData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve request');
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    try {
      await timeOffService.updateTimeOffRequest(requestId.toString(), { status: 'rejected' });
      await refreshTimeOffData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject request');
    }
  };

  const handleCancelRequest = async (requestId: number) => {
    try {
      await timeOffService.deleteTimeOffRequest(requestId.toString());
      await refreshTimeOffData();
      setSnackbar({
        open: true,
        message: 'Time off request cancelled successfully',
        severity: 'success'
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel request');
    }
  };

  const handleBulkApprove = async (ids?: number[]) => {
    const idsToProcess = ids || selectedPendingRequests;
    if (idsToProcess.length === 0) return;
    try {
      await timeOffService.bulkUpdateTimeOffRequests(idsToProcess, 'approved');
      if (!ids) setSelectedPendingRequests([]);
      await refreshTimeOffData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk approve requests');
    }
  };

  const handleBulkReject = async (ids?: number[]) => {
    const idsToProcess = ids || selectedPendingRequests;
    if (idsToProcess.length === 0) return;
    try {
      await timeOffService.bulkUpdateTimeOffRequests(idsToProcess, 'rejected');
      if (!ids) setSelectedPendingRequests([]);
      await refreshTimeOffData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk reject requests');
    }
  };

  const handleSelectPendingRequest = (requestId: number, checked: boolean) => {
    setSelectedPendingRequests(prev =>
      checked
        ? [...prev, requestId]
        : prev.filter(id => id !== requestId)
    );
  };

  const handleSelectAllPending = (checked: boolean) => {
    if (checked) {
      const pendingIds = timeOffRequests.filter(r => r.status === 'pending').map(r => r.id);
      setSelectedPendingRequests(pendingIds);
    } else {
      setSelectedPendingRequests([]);
    }
  };

  const handleSelectApprovedRequest = (requestId: number, checked: boolean) => {
    setSelectedApprovedRequests(prev =>
      checked
        ? [...prev, requestId]
        : prev.filter(id => id !== requestId)
    );
  };

  const handleSelectAllApproved = (checked: boolean) => {
    if (checked) {
      const currentMonth = calendarMonth.getMonth() + 1;
      const currentYear = calendarMonth.getFullYear();
      const approvedIds = timeOffRequests.filter(r =>
        r.status === 'approved' &&
        new Date(r.date).getMonth() + 1 === currentMonth &&
        new Date(r.date).getFullYear() === currentYear
      ).map(r => r.id);
      setSelectedApprovedRequests(approvedIds);
    } else {
      setSelectedApprovedRequests([]);
    }
  };

  const handleBulkDisapprove = async (ids?: number[]) => {
    const idsToProcess = ids || selectedApprovedRequests;
    if (idsToProcess.length === 0) return;
    try {
      await timeOffService.bulkUpdateTimeOffRequests(idsToProcess, 'rejected');
      if (!ids) setSelectedApprovedRequests([]);
      await refreshTimeOffData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disapprove requests');
    }
  };

  const handleGenerateMonthlyReport = () => setExportDialogOpen(true);

  // Event management handlers
  const handleCreateEvent = () => {
    setEditingEvent(null);
    setEventModalOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setEventModalOpen(true);
  };

  const handleEventSubmit = async (eventData: {
    title: string;
    description?: string;
    event_date: string;
    affected_department?: number;
  }) => {
    try {
      if (editingEvent) {
        await timeOffService.updateEvent(editingEvent.id, eventData);
        setSnackbar({
          open: true,
          message: 'Event updated successfully',
          severity: 'success'
        });
      } else {
        await timeOffService.createEvent(eventData);
        setSnackbar({
          open: true,
          message: 'Event created successfully',
          severity: 'success'
        });
      }
      // Refresh events
      const eventsData = await timeOffService.getEvents();
      setEvents(eventsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save event');
    }
  };

  const handleDeleteEvent = async (eventId: number) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await timeOffService.deleteEvent(eventId);
        setSnackbar({
          open: true,
          message: 'Event deleted successfully',
          severity: 'success'
        });
        // Refresh events
        const eventsData = await timeOffService.getEvents();
        setEvents(eventsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete event');
      }
    }
  };

  const handleExportReport = async () => {
    try {
      let url = '';
      let filename = '';

      if (exportType === 'month') {
        url = `/api/hr/holiday-report/generate?year=${exportYear}&month=${exportMonth}`;
        filename = `monthly_holiday_report_${exportYear}_${exportMonth.toString().padStart(2, '0')}.pdf`;
      } else {
        if (!exportStartDate || !exportEndDate) {
          setSnackbar({ open: true, message: 'Please select start and end dates', severity: 'error' });
          return;
        }
        url = `/api/hr/holiday-report/export?start_date=${exportStartDate}&end_date=${exportEndDate}`;
        filename = `holiday_report_${exportStartDate}_to_${exportEndDate}.pdf`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        openFilePreviewOrDownload(blob, filename, 'application/pdf');

        setSnackbar({ open: true, message: 'Holiday report generated successfully', severity: 'success' });
        setExportDialogOpen(false);
      } else {
        const errorData = await response.json();
        setSnackbar({ open: true, message: errorData.error || 'Failed to generate report', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to generate report', severity: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getTimeOffTypeLabel = (type: string, halfDayPeriod?: string) => {
    switch (type) {
      case 'full_day': return 'Full Day';
      case 'half_day': return halfDayPeriod === 'morning' ? 'Half Day (AM)' : 'Half Day (PM)';
      case 'remote': return 'Remote';
      default: return type;
    }
  };

  const selectedDateRequests = selectedDates.length > 0
    ? timeOffRequests.filter(req => selectedDates.includes(req.date) && req.status === 'approved')
    : [];

  // Map user's office ID to country for components that need the country string
  const userOfficeCountry = offices.find(office => Number(office.id) === Number(user?.user_office))?.country;

  const getGroupedRequests = (requests: TimeOffRequest[]) => {
    const groups: Record<string, TimeOffRequest[]> = {};

    requests.forEach(req => {
      const key = `${req.userId}_${req.createdAt}_${req.holidayType}_${req.notes || ''}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(req);
    });

    return Object.values(groups).sort((a, b) =>
      new Date(b[0].createdAt).getTime() - new Date(a[0].createdAt).getTime()
    );
  };

  const groupConsecutiveDates = (requests: TimeOffRequest[]): Array<{ startDate: string; endDate: string; requests: TimeOffRequest[] }> => {
    if (requests.length === 0) return [];

    const sortedRequests = [...requests].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const groups: Array<{ startDate: string; endDate: string; requests: TimeOffRequest[] }> = [];
    let currentGroup: TimeOffRequest[] = [sortedRequests[0]];

    for (let i = 1; i < sortedRequests.length; i++) {
      const currentRequest = sortedRequests[i];
      const previousRequest = sortedRequests[i - 1];

      const currentDate = new Date(currentRequest.date);
      const previousDate = new Date(previousRequest.date);
      const dayDiff = (currentDate.getTime() - previousDate.getTime()) / (1000 * 60 * 60 * 24);

      if (dayDiff <= 1) {
        currentGroup.push(currentRequest);
      } else {
        if (currentGroup.length > 0) {
          groups.push({
            startDate: currentGroup[0].date,
            endDate: currentGroup[currentGroup.length - 1].date,
            requests: currentGroup,
          });
        }
        currentGroup = [currentRequest];
      }
    }

    if (currentGroup.length > 0) {
      groups.push({
        startDate: currentGroup[0].date,
        endDate: currentGroup[currentGroup.length - 1].date,
        requests: currentGroup,
      });
    }

    return groups;
  };

  const userRequestGroups = groupConsecutiveDates(userTimeOffRequests);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        gap: 2
      }}>
        <LoadingAnimation />
        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
          Loading time off calendar...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 1,
            border: `1px solid ${panelBorder}`,
          }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, md: 3, lg: 4 }}>
        {/* Calendar Section */}
        <Grid item xs={12} md={8}>
          <TimeOffCalendar
            selectedDates={selectedDates}
            onDateSelect={handleDateSelect}
            timeOffRequests={timeOffRequests}
            publicHolidays={publicHolidays}
            events={events}
            birthdays={birthdays}
            disablePublicHolidays={true}
            userOffice={userOfficeCountry}
            onApproveRequest={handleApproveRequest}
            onRejectRequest={handleRejectRequest}
            onMonthChange={handleMonthChange}
          />
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Export Holiday Report Button */}
            {user?.roles.some(role => role === ROLE.ADMIN || role === ROLE.HR) && (
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleGenerateMonthlyReport}
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: 1,
                  fontWeight: 600,
                  borderColor: panelBorder,
                  color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  '&:hover': {
                    borderColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                    bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04),
                  },
                  ...responsiveFontSize
                }}
              >
                Export Holiday Report
              </Button>
            )}

            {/* Request Time Off Button */}
            {selectedDates.length > 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleModalOpen}
                size="large"
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: 1,
                  fontWeight: 600,
                  bgcolor: NAUTILUS.sand,
                  color: NAUTILUS.deepOcean,
                  boxShadow: 'none',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  fontSize: '0.8rem',
                  '&:hover': {
                    bgcolor: alpha(NAUTILUS.sand, 0.85),
                    boxShadow: 'none',
                  },
                  transition: 'background-color 0.15s ease',
                  ...responsiveFontSize
                }}
              >
                Request Time Off
              </Button>
            )}

            {/* Recurring Selection Button */}
            <Button
              variant="outlined"
              startIcon={<RepeatIcon />}
              onClick={() => setRecurringModalOpen(true)}
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: 1,
                fontWeight: 600,
                borderColor: panelBorder,
                color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                '&:hover': {
                  borderColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                  bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04),
                },
              }}
            >
              Select Recurring Dates
            </Button>

            {/* Selected Dates Details */}
            {selectedDates.length > 0 && (
              <Card
                sx={{
                  borderRadius: 1,
                  boxShadow: 'none',
                  border: `1px solid ${panelBorder}`,
                  bgcolor: surfaceBg,
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          bgcolor: isDark ? alpha(NAUTILUS.sand, 0.15) : alpha(NAUTILUS.deepOcean, 0.08),
                          mr: 2
                        }}
                      >
                        <ScheduleIcon sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean, ...responsiveFontSize }}>
                          {selectedDates.length === 1
                            ? `Selected Date`
                            : `${selectedDates.length} Selected Dates`
                          }
                        </Typography>
                        {selectedDates.length === 1 && (
                          <Typography variant="body2" color="text.secondary">
                            {new Date(selectedDates[0]).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Tooltip title="Clear Selection">
                      <IconButton onClick={handleClearSelection} size="small" color="error">
                        <DeleteSweepIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {selectedDateRequests.length > 0 ? (
                    <List dense sx={{ mb: 2 }}>
                      {selectedDateRequests.map((request) => (
                        <ListItem
                          key={request.id}
                          sx={{
                            px: 0,
                            py: 1,
                            borderRadius: 1,
                            '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) }
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.8),
                                width: 32,
                                height: 32
                              }}
                            >
                              <PersonIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 500, ...responsiveFontSize }}>
                                {request.userName}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {getTimeOffTypeLabel(request.holidayType, request.halfDayPeriod)}
                                </Typography>
                                <Chip
                                  label={request.status}
                                  size="small"
                                  color={getStatusColor(request.status)}
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No approved time off requests for selected dates
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}


            {/* Pending Requests Accordion (Admin Only) */}
            {hasCapability(user, CAP.APPROVE_TIME_OFF) && timeOffRequests.filter(r => r.status === 'pending').length > 0 && (
              <Accordion
                expanded={pendingRequestsExpanded}
                onChange={() => setPendingRequestsExpanded(!pendingRequestsExpanded)}
                sx={{
                  boxShadow: 'none',
                  border: `1px solid ${panelBorder}`,
                  borderRadius: 1,
                  bgcolor: surfaceBg,
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />}
                  aria-controls="pending-requests-content"
                  id="pending-requests-header"
                  sx={{
                    px: 3,
                    py: 1.5,
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                    },
                    '&:hover': {
                      bgcolor: isDark ? alpha(NAUTILUS.sand, 0.04) : alpha(NAUTILUS.deepOcean, 0.03)
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: theme.palette.warning.main,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>
                      Pending Requests
                    </Typography>
                    <Chip
                      label={timeOffRequests.filter(r => r.status === 'pending').length}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        color: theme.palette.warning.main,
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                  <Box sx={{ mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleBulkApprove()}
                      disabled={selectedPendingRequests.length === 0}
                      size="small"
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Approve Selected ({selectedPendingRequests.length})
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => handleBulkReject()}
                      disabled={selectedPendingRequests.length === 0}
                      size="small"
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Reject Selected ({selectedPendingRequests.length})
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Checkbox
                      checked={selectedPendingRequests.length === timeOffRequests.filter(r => r.status === 'pending').length && timeOffRequests.filter(r => r.status === 'pending').length > 0}
                      indeterminate={selectedPendingRequests.length > 0 && selectedPendingRequests.length < timeOffRequests.filter(r => r.status === 'pending').length}
                      onChange={(e) => handleSelectAllPending(e.target.checked)}
                      sx={{
                        color: theme.palette.primary.main,
                        '&.Mui-checked': { color: theme.palette.primary.main }
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
                      Select All Pending Requests
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {getGroupedRequests(timeOffRequests.filter(r => r.status === 'pending')).map((group, groupIndex) => (
                      <Card key={groupIndex} variant="outlined" sx={{ borderRadius: 1, borderColor: panelBorder }}>
                        <Box sx={{
                          p: 2,
                          bgcolor: isDark ? alpha(NAUTILUS.sand, 0.04) : alpha(NAUTILUS.deepOcean, 0.03),
                          borderBottom: `1px solid ${panelBorder}`,
                          display: 'flex',
                          alignItems: { xs: 'stretch', sm: 'flex-start' },
                          justifyContent: 'space-between',
                          flexDirection: { xs: 'column', sm: 'row' },
                          flexWrap: 'wrap',
                          gap: 1.5
                        }}>
                          <Box sx={{ minWidth: 0, flex: '1 1 180px' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {group[0].userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {group.length} request{group.length > 1 ? 's' : ''} • {getTimeOffTypeLabel(group[0].holidayType, group[0].halfDayPeriod)}
                            </Typography>
                            {(() => {
                              const summary = leaveSummaries[String(group[0].userId)];
                              const paidLeave = summary?.paid_leave ?? 0;
                              const awaiting = summary?.leave_awaiting_deduction ?? 0;
                              return (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                                  <Chip
                                    size="small"
                                    label={`Paid leave ${paidLeave.toFixed(1)}`}
                                    color={paidLeave >= 0 ? 'success' : 'error'}
                                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                                  />
                                  <Tooltip title="Days requested (pending or approved) that are not yet subtracted from the balance. They are posted on the 28th of each month, together with the monthly credit. Leave after the 28th waits until the following 28th.">
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      color={awaiting > 0 ? 'warning' : 'default'}
                                      icon={<InfoOutlinedIcon sx={{ fontSize: '0.85rem !important' }} />}
                                      label={`Awaiting deduction ${awaiting.toFixed(1)}`}
                                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, maxWidth: '100%' }}
                                    />
                                  </Tooltip>
                                </Box>
                              );
                            })()}
                          </Box>
                          <Box sx={{
                            display: 'flex',
                            gap: 1,
                            flexShrink: 0,
                            width: { xs: '100%', sm: 'auto' },
                            '& > button': { flex: { xs: 1, sm: 'none' } }
                          }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => handleBulkApprove(group.map(r => r.id))}
                              sx={{ textTransform: 'none', borderRadius: 1, boxShadow: 'none' }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleBulkReject(group.map(r => r.id))}
                              sx={{ textTransform: 'none', borderRadius: 1 }}
                            >
                              Reject
                            </Button>
                          </Box>
                        </Box>
                        <List dense sx={{ p: 0 }}>
                          {group.map((request) => (
                            <ListItem
                              key={request.id}
                              sx={{
                                px: 2,
                                py: 1,
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                '&:last-child': { borderBottom: 'none' },
                                '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) }
                              }}
                            >
                              <Checkbox
                                checked={selectedPendingRequests.includes(request.id)}
                                onChange={(e) => handleSelectPendingRequest(request.id, e.target.checked)}
                                size="small"
                                sx={{
                                  color: theme.palette.primary.main,
                                  '&.Mui-checked': { color: theme.palette.primary.main },
                                  mr: 1
                                }}
                              />
                              <ListItemText
                                primary={
                                  <Typography variant="body2">
                                    {new Date(request.date).toLocaleDateString('en-US', {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </Typography>
                                }
                                secondary={
                                  request.notes && (
                                    <Typography variant="caption" color="text.secondary">
                                      "{request.notes}"
                                    </Typography>
                                  )
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Card>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Approved Requests Accordion (Admin Only) */}
            {hasCapability(user, CAP.APPROVE_TIME_OFF) && (() => {
              const currentMonth = calendarMonth.getMonth() + 1;
              const currentYear = calendarMonth.getFullYear();
              const currentMonthApproved = timeOffRequests.filter(r =>
                r.status === 'approved' &&
                new Date(r.date).getMonth() + 1 === currentMonth &&
                new Date(r.date).getFullYear() === currentYear
              );
              return currentMonthApproved.length > 0;
            })() && (
                <Accordion
                  expanded={approvedRequestsExpanded}
                  onChange={() => setApprovedRequestsExpanded(!approvedRequestsExpanded)}
                  sx={{
                    boxShadow: 'none',
                    border: `1px solid ${panelBorder}`,
                    borderRadius: 1,
                    bgcolor: surfaceBg,
                    overflow: 'hidden',
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />}
                    aria-controls="approved-requests-content"
                    id="approved-requests-header"
                    sx={{
                      px: 3,
                      py: 1.5,
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                      },
                      '&:hover': {
                        bgcolor: isDark ? alpha(NAUTILUS.sand, 0.04) : alpha(NAUTILUS.deepOcean, 0.03)
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: theme.palette.success.main,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                          }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>
                          Approved Requests
                        </Typography>
                      <Chip
                        label={(() => {
                          const currentMonth = calendarMonth.getMonth() + 1;
                          const currentYear = calendarMonth.getFullYear();
                          return timeOffRequests.filter(r =>
                            r.status === 'approved' &&
                            new Date(r.date).getMonth() + 1 === currentMonth &&
                            new Date(r.date).getFullYear() === currentYear
                          ).length;
                        })()}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                    <Box sx={{ mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => handleBulkDisapprove()}
                        disabled={selectedApprovedRequests.length === 0}
                        size="small"
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          fontWeight: 600,
                          boxShadow: 'none',
                          '&:hover': {
                            boxShadow: 'none',
                          },
                        }}
                      >
                        Disapprove Selected ({selectedApprovedRequests.length})
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {(() => {
                        const currentMonth = calendarMonth.getMonth() + 1;
                        const currentYear = calendarMonth.getFullYear();
                        const currentMonthApproved = timeOffRequests.filter(r =>
                          r.status === 'approved' &&
                          new Date(r.date).getMonth() + 1 === currentMonth &&
                          new Date(r.date).getFullYear() === currentYear
                        );
                        return (
                          <>
                            <Checkbox
                              checked={selectedApprovedRequests.length === currentMonthApproved.length && currentMonthApproved.length > 0}
                              indeterminate={selectedApprovedRequests.length > 0 && selectedApprovedRequests.length < currentMonthApproved.length}
                              onChange={(e) => handleSelectAllApproved(e.target.checked)}
                              sx={{
                                color: theme.palette.primary.main,
                                '&.Mui-checked': { color: theme.palette.primary.main }
                              }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
                              Select All Approved Requests
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {(() => {
                        const currentMonth = calendarMonth.getMonth() + 1;
                        const currentYear = calendarMonth.getFullYear();
                        const currentMonthApproved = timeOffRequests.filter(r =>
                          r.status === 'approved' &&
                          new Date(r.date).getMonth() + 1 === currentMonth &&
                          new Date(r.date).getFullYear() === currentYear
                        );
                        return getGroupedRequests(currentMonthApproved).map((group, groupIndex) => (
                          <Card key={groupIndex} variant="outlined" sx={{ borderRadius: 1, borderColor: panelBorder }}>
                            <Box sx={{
                              p: 2,
                              bgcolor: isDark ? alpha(NAUTILUS.sand, 0.04) : alpha(NAUTILUS.deepOcean, 0.03),
                              borderBottom: `1px solid ${panelBorder}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              flexWrap: 'wrap',
                              gap: 2
                            }}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {group[0].userName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {group.length} request{group.length > 1 ? 's' : ''} • {getTimeOffTypeLabel(group[0].holidayType, group[0].halfDayPeriod)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleBulkDisapprove(group.map(r => r.id))}
                                  sx={{ textTransform: 'none', borderRadius: 1 }}
                                >
                                  Disapprove
                                </Button>
                              </Box>
                            </Box>
                            <List dense sx={{ p: 0 }}>
                              {group.map((request) => (
                                <ListItem
                                  key={request.id}
                                  sx={{
                                    px: 2,
                                    py: 1,
                                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                    '&:last-child': { borderBottom: 'none' },
                                    '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) }
                                  }}
                                >
                                  <Checkbox
                                    checked={selectedApprovedRequests.includes(request.id)}
                                    onChange={(e) => handleSelectApprovedRequest(request.id, e.target.checked)}
                                    size="small"
                                    sx={{
                                      color: theme.palette.primary.main,
                                      '&.Mui-checked': { color: theme.palette.primary.main },
                                      mr: 1
                                    }}
                                  />
                                  <ListItemText
                                    primary={
                                      <Typography variant="body2">
                                        {new Date(request.date).toLocaleDateString('en-US', {
                                          weekday: 'short',
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </Typography>
                                    }
                                    secondary={
                                      request.notes && (
                                        <Typography variant="caption" color="text.secondary">
                                          "{request.notes}"
                                        </Typography>
                                      )
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Card>
                        ));
                      })()}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

            {/* Events Management (Admin Only) */}
            {hasCapability(user, CAP.APPROVE_TIME_OFF) && (
              <Card
                sx={{
                  borderRadius: 1,
                  boxShadow: 'none',
                  border: `1px solid ${panelBorder}`,
                  bgcolor: surfaceBg,
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? '#b0bec5' : '#546e7a' }}>
                      Company Events
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleCreateEvent}
                      sx={{
                        borderRadius: 1,
                        fontWeight: 600,
                        boxShadow: 'none',
                        bgcolor: NAUTILUS.sand,
                        color: NAUTILUS.deepOcean,
                        '&:hover': {
                          bgcolor: alpha(NAUTILUS.sand, 0.85),
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Add Event
                    </Button>
                  </Box>

                  {(() => {
                    const currentMonthEvents = events.filter(event => {
                      const eventDate = new Date(event.event_date);
                      return eventDate.getMonth() === calendarMonth.getMonth() &&
                        eventDate.getFullYear() === calendarMonth.getFullYear();
                    });
                    return currentMonthEvents.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {currentMonthEvents.slice(0, 3).map((event) => (
                          <Box
                            key={event.id}
                            sx={{
                              p: 2,
                              borderRadius: 1,
                              bgcolor: isDark ? alpha(NAUTILUS.sand, 0.04) : alpha(NAUTILUS.deepOcean, 0.03),
                              border: `1px solid ${panelBorder}`
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {event.title}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditEvent(event)}
                                  sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}
                                >
                                  <ScheduleIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteEvent(event.id)}
                                  sx={{ color: theme.palette.error.main }}
                                >
                                  <DeleteSweepIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {new Date(event.event_date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                              {event.affected_department && ` • ${event.affected_department}`}
                            </Typography>
                            {event.description && (
                              <Typography variant="body2" color="text.secondary">
                                {event.description}
                              </Typography>
                            )}
                          </Box>
                        ))}
                        {currentMonthEvents.length > 3 && (
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                            +{currentMonthEvents.length - 3} more events
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No events scheduled for {monthNames[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Create your first company event
                        </Typography>
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Current User Summary */}
            {user && (
              <Card
                sx={{
                  borderRadius: 1,
                  boxShadow: 'none',
                  border: `1px solid ${panelBorder}`,
                  bgcolor: surfaceBg,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s ease',
                  '&:hover': {
                    borderColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${isDark ? NAUTILUS.sand : NAUTILUS.deepOcean}`,
                    outlineOffset: 2,
                  },
                }}
                onClick={handleUserRequestsModalOpen}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleUserRequestsModalOpen();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="View your time off requests"
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? '#b0bec5' : '#546e7a', ...responsiveFontSize }}>
                      Your Time Off
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUserRequestsModalOpen();
                      }}
                    >
                      <PersonIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                    <Chip
                      label={`${userTimeOffRequests.length} Total`}
                      sx={{
                        bgcolor: isDark ? alpha(NAUTILUS.sand, 0.1) : alpha(NAUTILUS.deepOcean, 0.08),
                        color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                        fontWeight: 500,
                        border: `1px solid ${panelBorder}`,
                        ...responsiveFontSize
                      }}
                    />
                    <Chip
                      label={`${userTimeOffRequests.filter(r => r.status === 'approved').length} Approved`}
                      color="success"
                      sx={{
                        fontWeight: 500
                      }}
                    />
                    {userTimeOffRequests.filter(r => r.status === 'pending').length > 0 && (
                      <Chip
                        label={`${userTimeOffRequests.filter(r => r.status === 'pending').length} Pending`}
                        color="warning"
                        sx={{
                          fontWeight: 500,
                          ...responsiveFontSize
                        }}
                      />
                    )}
                  </Box>

                  {/* Paid Leave Balance */}
                  {user && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a', ...responsiveFontSize }}>
                        Paid Leave Balance
                      </Typography>
                      {(() => {
                        const currentYear = new Date().getFullYear();
                        const approvedThisYear = userTimeOffRequests.filter(r => r.status === 'approved' && new Date(r.date).getFullYear() === currentYear && ['paid_leave', 'sick_leave', 'part_time'].includes(r.hrCategory));
                        const pendingThisYear = userTimeOffRequests.filter(r => r.status === 'pending' && new Date(r.date).getFullYear() === currentYear && ['paid_leave', 'sick_leave', 'part_time'].includes(r.hrCategory));
                        const calculateDays = (requests: TimeOffRequest[]) => requests.reduce((total, req) => {
                          if (req.holidayType === 'full_day' || req.holidayType === 'remote') return total + 1;
                          if (req.holidayType === 'half_day') return total + 0.5;
                          return total;
                        }, 0);
                        const takenDays = calculateDays(approvedThisYear);
                        const pendingDays = calculateDays(pendingThisYear);
                        const paidLeaveBalance = holidayBalances.find(b => b.year === currentYear && b.category === 'paid_leave')?.balance || 0;
                        const remaining = paidLeaveBalance;
                        return (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Holidays Taken So Far
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                {takenDays.toFixed(1)} days
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Not yet approved Requests
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                {pendingDays.toFixed(1)} days
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Remaining Holiday Balance
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                {remaining.toFixed(1)} days
                              </Typography>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  )}

                  {/* R&R Balance */}
                  {user && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a', ...responsiveFontSize }}>
                        R&R Balance
                      </Typography>
                      {(() => {
                        const currentYear = new Date().getFullYear();
                        const approvedThisYear = userTimeOffRequests.filter(r => r.status === 'approved' && new Date(r.date).getFullYear() === currentYear && r.hrCategory === 'rr');
                        const pendingThisYear = userTimeOffRequests.filter(r => r.status === 'pending' && new Date(r.date).getFullYear() === currentYear && r.hrCategory === 'rr');
                        const calculateDays = (requests: TimeOffRequest[]) => requests.reduce((total, req) => {
                          if (req.holidayType === 'full_day' || req.holidayType === 'remote') return total + 1;
                          if (req.holidayType === 'half_day') return total + 0.5;
                          return total;
                        }, 0);
                        const takenDays = calculateDays(approvedThisYear);
                        const pendingDays = calculateDays(pendingThisYear);
                        const rrBalance = holidayBalances.find(b => b.year === currentYear && b.category === 'rr')?.balance || 0;
                        const remaining = rrBalance;
                        return (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                R&R Taken So Far
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                {takenDays.toFixed(1)} days
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Not yet approved Requests
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                {pendingDays.toFixed(1)} days
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Remaining R&R Balance
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                {remaining.toFixed(1)} days
                              </Typography>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  )}

                  {/* Show date ranges */}
                  {userRequestGroups.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>
                        Recent Activity
                      </Typography>
                      {userRequestGroups.slice(0, 2).map((group, index) => {
                        const startDate = new Date(group.startDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        });
                        const endDate = new Date(group.endDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        });

                        const dateRange = group.startDate === group.endDate
                          ? startDate
                          : `${startDate} - ${endDate}`;

                        const approvedCount = group.requests.filter(r => r.status === 'approved').length;
                        const pendingCount = group.requests.filter(r => r.status === 'pending').length;

                        return (
                          <Box key={index} sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: isDark ? alpha(NAUTILUS.sand, 0.04) : alpha(NAUTILUS.deepOcean, 0.02), border: `1px solid ${panelBorder}` }}>
                            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 1 }}>
                              {dateRange}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {approvedCount > 0 && (
                                <Chip
                                  label={`${approvedCount} approved`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: alpha(theme.palette.success.main, 0.8),
                                    color: 'white',
                                    fontWeight: 600
                                  }}
                                />
                              )}
                              {pendingCount > 0 && (
                                <Chip
                                  label={`${pendingCount} pending`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: alpha(theme.palette.warning.main, 0.8),
                                    color: 'white',
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                      {userRequestGroups.length > 2 && (
                        <Typography variant="caption" sx={{ opacity: 0.8, fontStyle: 'italic' }}>
                          +{userRequestGroups.length - 2} more periods
                        </Typography>
                      )}
                    </Box>
                  )}

                  {userRequestGroups.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body2" sx={{ color: isDark ? '#b0bec5' : '#546e7a' }}>
                        No time off requests yet
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? '#78909c' : '#78909c', mt: 0.5, display: 'block' }}>
                        Select dates on the calendar to request time off
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        </Grid >
      </Grid >

      {/* Floating Action Button for Mobile */}
      {
        isMobile && selectedDates.length > 0 && (
          <Fab
            aria-label="Request time off for selected dates"
            onClick={handleModalOpen}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              width: 56,
              height: 56,
              borderRadius: 1,
              bgcolor: NAUTILUS.sand,
              color: NAUTILUS.deepOcean,
              boxShadow: 'none',
              border: `1px solid ${panelBorder}`,
              '&:hover': {
                bgcolor: alpha(NAUTILUS.sand, 0.85),
                boxShadow: 'none',
              },
            }}
          >
            <AddIcon />
          </Fab>
        )
      }

      {/* Request Modal */}
      <TimeOffRequestModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleRequestSubmit}
        initialDates={selectedDates}
        userId={user?.id || 0}
        userName={user?.name || ''}
        isAdmin={hasCapability(user, CAP.APPROVE_TIME_OFF)}
        users={users}
        publicHolidays={publicHolidays}
        userOffice={userOfficeCountry}
      />

      {/* Recurring Date Selection Modal */}
      <RecurringDateSelectionModal
        open={recurringModalOpen}
        onClose={() => setRecurringModalOpen(false)}
        onSelectDates={handleRecurringDatesSelected}
      />

      {/* User Requests Modal */}
      <Dialog
        open={userRequestsModalOpen}
        onClose={handleUserRequestsModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            bgcolor: surfaceBg,
            border: `1px solid ${panelBorder}`,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${panelBorder}`, py: 1.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: isDark ? alpha(NAUTILUS.sand, 0.15) : alpha(NAUTILUS.deepOcean, 0.08)
                }}
              >
                <PersonIcon sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />
              </Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>
                Your Time Off Requests
              </Typography>
            </Box>
            <IconButton
              onClick={handleUserRequestsModalClose}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.5)
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {userRequestGroups.length > 0 ? (
            <Box>
              {userRequestGroups.map((group, index) => {
                const startDate = new Date(group.startDate);
                const endDate = new Date(group.endDate);

                const startDateFormatted = startDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });

                const endDateFormatted = endDate.toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                });

                const dateRangeText = group.startDate === group.endDate
                  ? startDateFormatted
                  : `${startDateFormatted} - ${endDateFormatted}`;

                const approvedCount = group.requests.filter(r => r.status === 'approved').length;
                const pendingCount = group.requests.filter(r => r.status === 'pending').length;
                const rejectedCount = group.requests.filter(r => r.status === 'rejected').length;

                return (
                  <Card key={index} sx={{ mb: 2, borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        {dateRangeText}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        {approvedCount > 0 && (
                          <Chip
                            label={`${approvedCount} Approved`}
                            color="success"
                            size="small"
                          />
                        )}
                        {pendingCount > 0 && (
                          <Chip
                            label={`${pendingCount} Pending`}
                            color="warning"
                            size="small"
                          />
                        )}
                        {rejectedCount > 0 && (
                          <Chip
                            label={`${rejectedCount} Rejected`}
                            color="error"
                            size="small"
                          />
                        )}
                      </Box>

                      <List dense>
                        {group.requests.map((request) => (
                          <ListItem key={request.id} sx={{ px: 0 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2">
                                    {getTimeOffTypeLabel(request.holidayType, request.halfDayPeriod)}
                                  </Typography>
                                  <Chip
                                    label={request.status}
                                    size="small"
                                    color={getStatusColor(request.status)}
                                  />
                                  {request.status === 'pending' && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() => handleCancelRequest(request.id)}
                                      sx={{ ml: 'auto', minWidth: 'auto', px: 1, py: 0.5, borderRadius: 1 }}
                                    >
                                      Cancel
                                    </Button>
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Requested on {new Date(request.createdAt).toLocaleDateString()}
                                  </Typography>
                                  {request.notes && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                      Note: {request.notes}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No time off requests found.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Click "Request Time Off" to submit your first request.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>
          <Button
            onClick={handleUserRequestsModalClose}
            sx={{
              borderRadius: 1,
              px: 3,
              color: 'text.secondary',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Holiday Report Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            bgcolor: surfaceBg,
            border: `1px solid ${panelBorder}`,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${panelBorder}`, py: 1.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: isDark ? alpha(NAUTILUS.sand, 0.15) : alpha(NAUTILUS.deepOcean, 0.08)
              }}
            >
              <FileDownloadIcon sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />
            </Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>
              Export Holiday Report
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, px: 3 }}>
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>Report Type</FormLabel>
            <RadioGroup
              row
              value={exportType}
              onChange={(e) => setExportType(e.target.value as 'month' | 'range')}
            >
              <FormControlLabel value="month" control={<Radio />} label="Monthly Report" />
              <FormControlLabel value="range" control={<Radio />} label="Custom Date Range" />
            </RadioGroup>
          </FormControl>

          {exportType === 'month' ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Year</InputLabel>
                <Select
                  value={exportYear}
                  label="Year"
                  onChange={(e) => setExportYear(Number(e.target.value))}
                  MenuProps={getZoomCompensatedSelectMenuProps()}
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Month</InputLabel>
                <Select
                  value={exportMonth}
                  label="Month"
                  onChange={(e) => setExportMonth(Number(e.target.value))}
                  MenuProps={getZoomCompensatedSelectMenuProps()}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <MenuItem key={month} value={month}>
                      {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                value={exportStartDate}
                onChange={(e) => setExportStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={exportEndDate}
                onChange={(e) => setExportEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}`, gap: 2 }}>
          <Button
            onClick={() => setExportDialogOpen(false)}
            sx={{
              borderRadius: 1,
              px: 3,
              color: 'text.secondary',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExportReport}
            variant="contained"
            startIcon={<FileDownloadIcon />}
            sx={{
              borderRadius: 1,
              px: 3,
              fontWeight: 600,
              bgcolor: NAUTILUS.sand,
              color: NAUTILUS.deepOcean,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: alpha(NAUTILUS.sand, 0.85),
                boxShadow: 'none',
              },
            }}
          >
            Export PDF
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Event Modal */}
      <EventModal
        open={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        onSubmit={handleEventSubmit}
        editingEvent={editingEvent}
        departments={departments}
      />

      {/* Recurring holiday exclusion warning */}
      <Dialog
        open={showExclusionWarning}
        onClose={handleCancelExclusion}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            bgcolor: surfaceBg,
            border: `1px solid ${panelBorder}`,
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${panelBorder}`, py: 1.5, px: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
            }}
          >
            Bank holidays excluded
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, color: isDark ? '#b0bec5' : '#546e7a' }}>
            {excludedDates.length} date{excludedDates.length === 1 ? '' : 's'} fall
            on bank holidays and cannot be selected. Continue with the remaining{' '}
            {filteredDates.length} date{filteredDates.length === 1 ? '' : 's'}?
          </Typography>
          {excludedDates.length > 0 && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: `1px solid ${panelBorder}`,
                bgcolor: isDark ? alpha(NAUTILUS.sand, 0.04) : alpha(NAUTILUS.deepOcean, 0.03),
                maxHeight: 160,
                overflowY: 'auto',
              }}
            >
              {excludedDates.map((date) => (
                <Typography key={date} variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                  {new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}`, gap: 1 }}>
          <Button onClick={handleCancelExclusion} sx={{ borderRadius: 1, color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExclusion}
            variant="contained"
            disabled={filteredDates.length === 0}
            sx={{
              borderRadius: 1,
              fontWeight: 600,
              bgcolor: NAUTILUS.sand,
              color: NAUTILUS.deepOcean,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: alpha(NAUTILUS.sand, 0.85),
                boxShadow: 'none',
              },
            }}
          >
            Use {filteredDates.length} date{filteredDates.length === 1 ? '' : 's'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default TimeOffPage;